import { useCallback, useEffect, useState } from 'react';
import api from '../API';
import styles from './ItemPicker.module.css';

/**
 * ItemPicker — modal for browsing Girder collections/folders and selecting
 * individual items or entire folders to add to a project.
 *
 * Props:
 *   onConfirm    — called with { itemIds: string[], folderIds: string[] }
 *   onCancel     — called when the modal is dismissed
 *   alreadyAdded — optional Set of item IDs already in the project (shown greyed)
 */
function ItemPicker({ onConfirm, onCancel, alreadyAdded = new Set() }) {
    // Navigation stack: each entry is { id, name, type } where type is
    // 'root' | 'collection' | 'folder'
    const [stack, setStack] = useState([{ id: null, name: 'Collections', type: 'root' }]);
    const [collections, setCollections] = useState([]);
    const [folders, setFolders] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    // selectedItems: Set of item IDs, selectedFolders: Set of folder IDs
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [selectedFolders, setSelectedFolders] = useState(new Set());

    const current = stack[stack.length - 1];

    const loadLevel = useCallback((level) => {
        setLoading(true);
        setCollections([]);
        setFolders([]);
        setItems([]);

        if (level.type === 'root') {
            api.get('collection', { query: { limit: 100 } })
                .then((r) => {
                    if (r.status === 200) setCollections(r.data || []);
                })
                .catch(() => {})
                .finally(() => setLoading(false));
        } else if (level.type === 'collection') {
            api.get('folder', { query: { parentType: 'collection', parentId: level.id, limit: 100 } })
                .then((r) => {
                    if (r.status === 200) setFolders(r.data || []);
                })
                .catch(() => {})
                .finally(() => setLoading(false));
        } else {
            // folder — load sub-folders and items in parallel
            Promise.all([
                api.get('folder', { query: { parentType: 'folder', parentId: level.id, limit: 100 } }),
                api.get('item', { query: { folderId: level.id, limit: 100 } }),
            ])
                .then(([fRes, iRes]) => {
                    if (fRes.status === 200) setFolders(fRes.data || []);
                    if (iRes.status === 200) setItems(iRes.data || []);
                })
                .catch(() => {})
                .finally(() => setLoading(false));
        }
    }, []);

    useEffect(() => {
        loadLevel(current);
    }, [current, loadLevel]);

    function navigateInto(entry) {
        setStack((s) => [...s, entry]);
    }

    function navigateTo(idx) {
        setStack((s) => s.slice(0, idx + 1));
    }

    function toggleItem(id) {
        setSelectedItems((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }

    function toggleFolder(id) {
        setSelectedFolders((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }

    function handleConfirm() {
        onConfirm({
            itemIds: Array.from(selectedItems),
            folderIds: Array.from(selectedFolders),
        });
    }

    const totalSelected = selectedItems.size + selectedFolders.size;

    return (
        <div className={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Add items">
                <div className={styles.header}>
                    <h3 className={styles.title}>Add Items</h3>
                    <button type="button" className={styles.closeButton} onClick={onCancel} aria-label="Close">✕</button>
                </div>

                {/* Breadcrumb */}
                <nav className={styles.breadcrumb} aria-label="Browser navigation">
                    {stack.map((entry, idx) => (
                        <span key={idx} className={styles.breadcrumbEntry}>
                            {idx > 0 && <span className={styles.breadcrumbSep}>/</span>}
                            {idx < stack.length - 1 ? (
                                <button
                                    type="button"
                                    className={styles.breadcrumbLink}
                                    onClick={() => navigateTo(idx)}
                                >
                                    {entry.name}
                                </button>
                            ) : (
                                <span className={styles.breadcrumbCurrent}>{entry.name}</span>
                            )}
                        </span>
                    ))}
                </nav>

                <div className={styles.body}>
                    {loading && <p className={styles.loadingMsg}>Loading…</p>}

                    {/* Collections (root level only) */}
                    {!loading && collections.map((c) => (
                        <div key={c._id} className={styles.row}>
                            <span className={styles.typeIcon}>📁</span>
                            <button
                                type="button"
                                className={styles.navButton}
                                onClick={() => navigateInto({ id: String(c._id), name: c.name, type: 'collection' })}
                            >
                                {c.name}
                            </button>
                            <span className={styles.typeBadge}>collection</span>
                        </div>
                    ))}

                    {/* Sub-folders */}
                    {!loading && folders.map((f) => {
                        const fid = String(f._id);
                        const checked = selectedFolders.has(fid);
                        return (
                            <div key={fid} className={`${styles.row} ${checked ? styles.rowSelected : ''}`}>
                                <input
                                    type="checkbox"
                                    className={styles.checkbox}
                                    checked={checked}
                                    onChange={() => toggleFolder(fid)}
                                    id={`folder-${fid}`}
                                    title="Select entire folder (all items will be copied)"
                                />
                                <span className={styles.typeIcon}>📂</span>
                                <button
                                    type="button"
                                    className={styles.navButton}
                                    onClick={() => navigateInto({ id: fid, name: f.name, type: 'folder' })}
                                >
                                    {f.name}
                                </button>
                                <span className={styles.typeBadge}>folder</span>
                            </div>
                        );
                    })}

                    {/* Items */}
                    {!loading && items.map((item) => {
                        const iid = String(item._id);
                        const alreadyIn = alreadyAdded.has(iid);
                        const checked = selectedItems.has(iid);
                        return (
                            <div
                                key={iid}
                                className={`${styles.row} ${alreadyIn ? styles.rowDimmed : ''} ${checked ? styles.rowSelected : ''}`}
                            >
                                <input
                                    type="checkbox"
                                    className={styles.checkbox}
                                    checked={checked}
                                    disabled={alreadyIn}
                                    onChange={() => !alreadyIn && toggleItem(iid)}
                                    id={`item-${iid}`}
                                />
                                <span className={styles.typeIcon}>🖼</span>
                                <label htmlFor={`item-${iid}`} className={styles.itemLabel}>
                                    {item.name}
                                    {alreadyIn && <span className={styles.alreadyBadge}> already added</span>}
                                </label>
                            </div>
                        );
                    })}

                    {!loading && collections.length === 0 && folders.length === 0 && items.length === 0 && (
                        <p className={styles.emptyMsg}>This location is empty.</p>
                    )}
                </div>

                <div className={styles.footer}>
                    <span className={styles.selectionSummary}>
                        {totalSelected > 0
                            ? `${totalSelected} selection${totalSelected > 1 ? 's' : ''}`
                            : 'Nothing selected'}
                    </span>
                    <div className={styles.footerActions}>
                        <button type="button" className={styles.buttonSecondary} onClick={onCancel}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={styles.buttonPrimary}
                            onClick={handleConfirm}
                            disabled={totalSelected === 0}
                        >
                            Add selected
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ItemPicker;
